import type { DateKey } from '@/domain/date';
import { dateRange } from '@/domain/date';
import type { Mark, PrayerId } from '@/domain/prayer';
import { isPrayerId, normaliseMark } from '@/domain/prayer';
import type { DayRecord } from '@/domain/streaks';
import { emptyDay } from '@/domain/streaks';
import { StorageError, type RecordsPort } from '@/ports/records';
import { readJson, writeJson } from './kv';

/** One key per day, so a long history never rewrites one enormous blob. */
const key = (date: DateKey) => `day.${date}`;

function coerceDay(date: DateKey, value: unknown): DayRecord {
  if (typeof value !== 'object' || value === null) return emptyDay(date);
  const marks: DayRecord['marks'] = {};
  for (const [prayer, raw] of Object.entries(value as Record<string, unknown>)) {
    if (!isPrayerId(prayer) || typeof raw !== 'object' || raw === null) continue;
    const m = raw as Record<string, unknown>;
    marks[prayer] = normaliseMark({
      prayed: m.prayed === true,
      onTime: m.onTime === true,
      jamaah: m.jamaah === true,
      takbirah: m.takbirah === true,
    });
  }
  return { date, marks };
}

export class LocalRecords implements RecordsPort {
  async day(date: DateKey): Promise<DayRecord> {
    return coerceDay(date, readJson<unknown>(key(date)));
  }

  async range(from: DateKey, to: DateKey): Promise<DayRecord[]> {
    return dateRange(from, to).map((d) => coerceDay(d, readJson<unknown>(key(d))));
  }

  async setMark(date: DateKey, prayer: PrayerId, mark: Mark): Promise<void> {
    const current = await this.day(date);
    const next = { ...current.marks, [prayer]: normaliseMark(mark) };
    // Reading may degrade to "nothing stored" quietly; writing may not. A full
    // quota or a browser with site data blocked would otherwise take a prayer
    // the child had just recorded and drop it without a word.
    if (!writeJson(key(date), next)) throw new StorageError();
  }
}
